import React, { useState, useEffect, useMemo } from 'react';
import { Container, Box, Typography, Grid, Chip, Select, MenuItem, FormControl, InputLabel, TextField, Pagination, Stack, IconButton, useMediaQuery } from '@mui/material';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import Loading from '../components/Loading';
import { productService, categoryService } from '../services/api';
// Icons intentionally minimal here; remove unused to satisfy linter

const PAGE_SIZE = 12;

const Catalogue = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('name-asc');
  const [page, setPage] = useState(1);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Initial fetch
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [pRes, cRes] = await Promise.all([
          productService.getAll(),
          categoryService.getAll()
        ]);
        setProducts(pRes.data || []);
        setCategories(cRes.data || []);
      } catch (e) {
        console.error('Error loading catalogue', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Apply category from query param
  useEffect(() => {
    const catParam = searchParams.get('category');
    if (catParam && categories.length) {
      // validate category id exists
      if (categories.some(c => String(c.id) === String(catParam))) {
        setFilterCategory(String(catParam));
        setPage(1);
      }
    }
  }, [searchParams, categories]);

  // Normaliser chaque produit (image + id catégorie potentielle)
  const normalizedProducts = useMemo(() => {
    const base = process.env.REACT_APP_API_URL || 'http://localhost:8080';
    return products.map(p => {
      const rawImage = p.image || (p.images && p.images[0]);
      const image = rawImage
        ? (/^https?:\/\//i.test(rawImage) || rawImage.startsWith('data:')
            ? rawImage
            : `${base.replace(/\/$/, '')}/${rawImage.replace(/^\//,'')}`)
        : 'https://via.placeholder.com/300x220?text=Produit';
      // Champ catégorie dans backend: p.categorie
      const catId = p.categorie_id || p.categorie || p.category_id || p.categoryId;
      return { ...p, _image: image, _catId: catId };
    });
  }, [products]);

  // Derived filtered + searched products
  const filteredProducts = useMemo(() => {
    let list = [...normalizedProducts];
    if (filterCategory) {
      list = list.filter(p => String(p._catId) === String(filterCategory));
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(p => (p.titre || '').toLowerCase().includes(q) || (p.description || '').toLowerCase().includes(q));
    }
    // sorting
    switch (sort) {
      case 'name-asc':
        list.sort((a,b) => (a.titre || '').localeCompare(b.titre || ''));
        break;
      case 'name-desc':
        list.sort((a,b) => (b.titre || '').localeCompare(a.titre || ''));
        break;
      case 'available-first':
        list.sort((a,b) => (b.disponibilite === 1 ? 1 : 0) - (a.disponibilite === 1 ? 1 : 0));
        break;
      default:
        break;
    }
    return list;
  }, [normalizedProducts, filterCategory, search, sort]);

  const pageCount = Math.max(1, Math.ceil(filteredProducts.length / PAGE_SIZE));
  const paginated = filteredProducts.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleCategoryChange = (id) => {
    setFilterCategory(id);
    setPage(1);
    // update query param for deep-link
    const params = new URLSearchParams(searchParams);
    if (id) params.set('category', id); else params.delete('category');
    navigate({ pathname: '/catalogue', search: params.toString() });
  };

  if (loading) return <Loading />;

  return (
    <Box sx={{ bgcolor: '#ffffff', minHeight: '100vh' }}>
      {/* Hero / Title */}
      <Box 
        sx={{ 
          background: 'linear-gradient(135deg, #0077B8 0%, #00ACEA 100%)', 
          py: { xs: 5, md: 7 }, 
          mb: 5,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 30% 50%, rgba(255,255,255,0.15) 0%, transparent 50%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="xl">
          <Typography 
            variant="overline" 
            sx={{ 
              color: '#fbbf24', 
              fontWeight: 800, 
              letterSpacing: '3px', 
              mb: 1, 
              display: 'block',
              fontSize: '0.75rem',
            }}
          >
            CATALOGUE PRODUITS
          </Typography>
          <Typography 
            variant="h2" 
            sx={{ 
              fontSize: { xs: '1.75rem', md: '2.5rem' }, 
              fontWeight: 900, 
              color: 'white', 
              mb: 1.5,
              lineHeight: 1.2,
            }}
          >
            Parcourir Notre Catalogue
          </Typography>
          <Typography 
            sx={{ 
              color: 'rgba(255,255,255,0.9)', 
              maxWidth: 700, 
              lineHeight: 1.7, 
              fontSize: { xs: '0.938rem', md: '1.063rem' },
            }}
          >
            Tous nos produits professionnels avec filtres par catégorie, recherche et tri.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ pb: 6 }}>
        {/* Filters */}
        <PaperFilters
          categories={categories}
          filterCategory={filterCategory}
          onCategoryChange={handleCategoryChange}
          search={search}
          onSearch={setSearch}
          sort={sort}
          onSort={setSort}
          resultsCount={filteredProducts.length}
        />

        <Box sx={{ mt: 5 }} />

        {filteredProducts.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" fontWeight={600}>
              Aucun produit ne correspond à votre recherche.
            </Typography>
          </Box>
        ) : (
          <>
            <Grid 
              container 
              spacing={3}
              alignItems="flex-start"
              justifyContent="flex-start"
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(1, 1fr)',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                  lg: 'repeat(4, 1fr)',
                },
                gap: 3,
                '& > .MuiGrid-item': {
                  width: '100%',
                  maxWidth: '100%',
                  height: '350px',
                  minHeight: '350px',
                  maxHeight: '350px',
                  padding: 0,
                },
                '& .MuiCard-root': {
                  width: '100%',
                  height: '100%',
                }
              }}
            >
              {paginated.map(p => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={p.id}>
                  <ProductCard product={{ ...p, image: p._image }} />
                </Grid>
              ))}
            </Grid>
            <Box sx={{ mt: 6, mb: 2, display: 'flex', justifyContent: 'center' }}>
              <Pagination
                count={pageCount}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
                size="large"
                siblingCount={1}
                boundaryCount={1}
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontWeight: 700,
                    fontSize: '1rem',
                    minWidth: 40,
                    height: 40,
                    borderRadius: '8px',
                  },
                  '& .Mui-selected': {
                    background: 'linear-gradient(135deg, #0077B8 0%, #00ACEA 100%)',
                    color: 'white',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #00ACEA 0%, #0077B8 100%)',
                    }
                  }
                }}
              />
            </Box>
          </>
        )}
      </Container>
    </Box>
  );
};

// Filters toolbar component
const PaperFilters = ({ categories, filterCategory, onCategoryChange, search, onSearch, sort, onSort, resultsCount }) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
  return (
    <Box 
      sx={{ 
        p: 3, 
        bgcolor: '#f8fafc', 
        borderRadius: '16px',
        border: '1px solid #e2e8f0',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {/* Desktop/Tablet: chips row. Mobile: handled as a dropdown below sorter */}
        {!isMobile && (
          <Box>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                fontWeight: 700, 
                color: '#0c4a6e', 
                mb: 1.5,
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                letterSpacing: '1px',
              }}
            >
              Catégories
            </Typography>
            <SlidingCategoryChips 
              categories={categories}
              activeId={filterCategory}
              onSelect={onCategoryChange}
            />
          </Box>
        )}
        
        {/* Search & Sort */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label="Rechercher un produit"
            variant="outlined"
            value={search}
            onChange={e => { onSearch(e.target.value); }}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                bgcolor: 'white',
              }
            }}
          />
          <FormControl sx={{ minWidth: { xs: '100%', md: 200 } }}>
            <InputLabel id="sort-label">Trier par</InputLabel>
            <Select
              labelId="sort-label"
              value={sort}
              label="Trier par"
              onChange={e => onSort(e.target.value)}
              sx={{
                borderRadius: '12px',
                bgcolor: 'white',
              }}
            >
              <MenuItem value="name-asc">Nom (A-Z)</MenuItem>
              <MenuItem value="name-desc">Nom (Z-A)</MenuItem>
              <MenuItem value="available-first">Disponibilité</MenuItem>
            </Select>
          </FormControl>
          {/* Mobile-only category dropdown placed below the sorter */}
          {isMobile && (
            <FormControl sx={{ minWidth: '100%' }}>
              <InputLabel id="category-mobile-label">Catégories</InputLabel>
              <Select
                labelId="category-mobile-label"
                value={filterCategory}
                label="Catégories"
                onChange={e => onCategoryChange(e.target.value)}
                sx={{ borderRadius: '12px', bgcolor: 'white' }}
              
              >
                <MenuItem value="">Toutes</MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>{c.titre}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>
        
        {/* Results count */}
        <Box>
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 700, 
              color: '#0077B8',
              fontSize: '0.938rem',
            }}
          >
            {resultsCount} produit{resultsCount > 1 ? 's' : ''} trouvé{resultsCount > 1 ? 's' : ''}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// Sliding window for category chips (shows up to 5 with arrows)
const SlidingCategoryChips = ({ categories = [], activeId = '', onSelect }) => {
  const [start, setStart] = useState(0);
  const total = categories.length;
  const windowSize = Math.min(5, Math.max(0, total));
  const showArrows = total > 5;

  const displayed = Array.from({ length: windowSize }, (_, i) => categories[(start + i) % (total || 1)]);

  const commonChipSx = {
    fontWeight: 700,
    borderRadius: '999px',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0,119,184,0.2)',
    },
    transition: 'all 0.2s ease',
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap', py: 1 }}>
      {/* Persistent "Toutes" chip */}
      <Chip
        label="Toutes"
        clickable
        onClick={() => onSelect && onSelect('')}
        color={!activeId ? 'primary' : 'default'}
        sx={commonChipSx}
      />

      {showArrows && (
        <IconButton
          onClick={() => setStart((s) => (s - 1 + total) % total)}
          size="small"
          sx={{
            bgcolor: 'white',
            border: '1px solid #e2e8f0',
            width: 34,
            height: 34,
            '&:hover': { bgcolor: '#f1f5f9' },
          }}
          aria-label="Précédent"
        >
          <FaChevronLeft size={14} />
        </IconButton>
      )}

      <Box sx={{ display: 'flex', gap: 1, overflowX: 'hidden', overflowY: 'visible', flexWrap: 'nowrap', py: 0.5 }}>
        {displayed.map((c) => (
          <Chip
            key={c?.id}
            label={c?.titre || ''}
            clickable
            onClick={() => c && onSelect && onSelect(String(c.id))}
            color={c && String(c.id) === String(activeId) ? 'primary' : 'default'}
            sx={commonChipSx}
          />
        ))}
      </Box>

      {showArrows && (
        <IconButton
          onClick={() => setStart((s) => (s + 1) % total)}
          size="small"
          sx={{
            bgcolor: 'white',
            border: '1px solid #e2e8f0',
            width: 34,
            height: 34,
            '&:hover': { bgcolor: '#f1f5f9' },
          }}
          aria-label="Suivant"
        >
          <FaChevronRight size={14} />
        </IconButton>
      )}
    </Box>
  );
};

export default Catalogue;
